import React from 'react';
import {
  MonitorHeartOutlined,
  CheckCircleOutlineOutlined,
  MemoryOutlined,
  StorageOutlined,
  NetworkCheckOutlined,
  RouterOutlined,
  TimelineOutlined,
  DnsOutlined,
  GridOnOutlined,
  PictureAsPdfOutlined,
  ArchiveOutlined,
  LanguageOutlined,
} from '@mui/icons-material';
import { Trans, useTranslation } from 'react-i18next';
import styles from './_shared.module.scss';

const CopyIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" aria-hidden>
    <rect x="128" y="128" width="336" height="336" rx="57" ry="57" strokeLinejoin="round" />
    <path
      d="M383.5 128l.5-24a56.16 56.16 0 00-56-56H112a64.19 64.19 0 00-64 64v216a56.16 56.16 0 0056 56h24"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SosGuide = () => {
  const { t } = useTranslation();

  const handleCopy = (text, event) => {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      textarea.setSelectionRange(0, 99999);
      document.execCommand('copy');
      document.body.removeChild(textarea);

      const button = event.currentTarget;
      button.classList.add(styles.copied);
      setTimeout(() => button.classList.remove(styles.copied), 2000);
    } catch (err) {
      console.error('복사 실패:', err);
    }
  };

  const CommandLine = ({ cmd, large = false }) => (
    <div className={`${styles.commandBlock} ${large ? styles.commandBlockLarge : ''}`}>
      <code>{cmd}</code>
      <button
        className={styles.copyButton}
        onClick={(event) => handleCopy(cmd, event)}
        aria-label={t('guide.common.copyCommand')}
      >
        <CopyIcon />
      </button>
    </div>
  );

  return (
    <div className={styles.page}>
      {/* Hero */}
      <section className={styles.hero}>
        <div className={styles.heroGridOverlay} />
        <div className={styles.heroInner}>
          <div>
            <span className={styles.eyebrow}>System Diagnostics</span>
            <h1 className={styles.heroTitle}>
              <span className={styles.brandGradient}>GWSOS</span> Guide
            </h1>
            <p className={styles.heroSubtitle}>
              {t('guide.sos.subtitle')}
            </p>
            <div className={styles.chipGroup}>
              <span className={`${styles.chip} ${styles.chipBrand}`}>
                <strong>RHEL / Rocky / CentOS</strong>&nbsp;7+
              </span>
              <span className={`${styles.chip} ${styles.chipBrand}`}>
                <strong>Ubuntu</strong>&nbsp;18+
              </span>
            </div>
          </div>
          <div className={styles.heroBadge}>
            <MonitorHeartOutlined />
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className={styles.section}>
        <header className={styles.sectionHeader}>
          <span className={styles.sectionEyebrow}>{t('guide.common.howToUse')}</span>
          <h2 className={styles.sectionTitle}>{t('guide.sos.stepsTitle')}</h2>
          <p className={styles.sectionLead}>
            {t('guide.sos.stepsLead')}
          </p>
        </header>

        <div className={styles.stepList}>
          {/* Step 1 */}
          <div className={styles.step}>
            <div className={styles.stepNumber}>1</div>
            <div className={styles.stepBody}>
              <h3 className={styles.stepTitle}>{t('guide.sos.step1Title')}</h3>
              <p className={styles.stepDesc}>
                {t('guide.sos.step1Desc')}
              </p>
              <p className={styles.stepDesc}><strong>{t('guide.sos.rhel7')}</strong></p>
              <CommandLine cmd="yum install -y sos" />
              <p className={styles.stepDesc}><strong>{t('guide.sos.rhel8')}</strong></p>
              <CommandLine cmd="dnf install -y sos" />
              <p className={styles.stepDesc}><strong>Ubuntu / Debian</strong></p>
              <CommandLine cmd="apt-get install -y sosreport" />
            </div>
          </div>

          {/* Step 2 */}
          <div className={styles.step}>
            <div className={styles.stepNumber}>2</div>
            <div className={styles.stepBody}>
              <h3 className={styles.stepTitle}>{t('guide.sos.step2Title')}</h3>
              <p className={styles.stepDesc}>
                <Trans i18nKey="guide.sos.step2Desc" />
              </p>
              <CommandLine cmd="sosreport" />
            </div>
          </div>

          {/* Step 3 */}
          <div className={styles.step}>
            <div className={styles.stepNumber}>3</div>
            <div className={styles.stepBody}>
              <h3 className={styles.stepTitle}>{t('guide.sos.step3Title')}</h3>
              <p className={styles.stepDesc}>
                {t('guide.sos.step3Desc')}
              </p>
              <p className={styles.stepDesc}><strong>{t('guide.sos.rhel')}</strong></p>
              <CommandLine cmd="cd /var/tmp" />
              <p className={styles.stepDesc}><strong>Ubuntu</strong></p>
              <CommandLine cmd="cd /tmp" />
              <div className={styles.callout}>
                <strong>{t('guide.sos.exampleName')}</strong>&nbsp;
                <code>sosreport-gw101-2024-01-09-fegprgj.tar.xz</code>
                <br />
                <Trans i18nKey="guide.sos.namingRule" />
              </div>
            </div>
          </div>

          {/* Step 4 */}
          <div className={styles.step}>
            <div className={styles.stepNumber}>4</div>
            <div className={styles.stepBody}>
              <h3 className={styles.stepTitle}>{t('guide.sos.step4Title')}</h3>
              <p className={styles.stepDesc}>
                <Trans i18nKey="guide.sos.step4Desc" />
              </p>
              <div className={`${styles.callout} ${styles.calloutBrand}`}>
                {t('guide.sos.step4Note')}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Report contents */}
      <section className={styles.section}>
        <header className={styles.sectionHeader}>
          <span className={styles.sectionEyebrow}>Report Contents</span>
          <h2 className={styles.sectionTitle}>{t('guide.sos.reportTitle')}</h2>
        </header>
        <div className={styles.pillGrid}>
          <div className={styles.pillCard}><DnsOutlined /><span className={styles.pillLabel}>{t('guide.sos.pillSystem')}</span></div>
          <div className={styles.pillCard}><MemoryOutlined /><span className={styles.pillLabel}>{t('guide.sos.pillMemory')}</span></div>
          <div className={styles.pillCard}><StorageOutlined /><span className={styles.pillLabel}>{t('guide.sos.pillDisk')}</span></div>
          <div className={styles.pillCard}><NetworkCheckOutlined /><span className={styles.pillLabel}>{t('guide.sos.pillNetwork')}</span></div>
          <div className={styles.pillCard}><RouterOutlined /><span className={styles.pillLabel}>{t('guide.sos.pillRouter')}</span></div>
          <div className={styles.pillCard}><TimelineOutlined /><span className={styles.pillLabel}>{t('guide.sos.pillTraffic')}</span></div>
        </div>
      </section>

      {/* Output formats */}
      <section className={styles.section}>
        <header className={styles.sectionHeader}>
          <span className={styles.sectionEyebrow}>Output Formats</span>
          <h2 className={styles.sectionTitle}>{t('guide.sos.outputTitle')}</h2>
        </header>
        <div className={styles.pillGrid}>
          <div className={styles.pillCard}><CheckCircleOutlineOutlined /><span className={styles.pillLabel}>{t('guide.sos.outSummary')}</span></div>
          <div className={styles.pillCard}><GridOnOutlined /><span className={styles.pillLabel}>{t('guide.sos.outExcel')}</span></div>
          <div className={styles.pillCard}><PictureAsPdfOutlined /><span className={styles.pillLabel}>{t('guide.sos.outPdf')}</span></div>
          <div className={styles.pillCard}><ArchiveOutlined /><span className={styles.pillLabel}>{t('guide.sos.outRaw')}</span></div>
          <div className={styles.pillCard}><LanguageOutlined /><span className={styles.pillLabel}>{t('guide.sos.outHtml')}</span></div>
        </div>
      </section>
    </div>
  );
};

export default SosGuide;
